import { createAction, createSelector } from "@reduxjs/toolkit";

import { ReduxRootState } from "types/redux-store";
import { AccountType } from "shared/types/accounts";
import { accountHasGroup, groupAccounts } from "./libs/accounts-groups";
import { getAccountPlatformType } from "./libs/account-platform";
import { sortAccounts } from "./libs/sort-accounts";

const ACCOUNTS_REPLACED = "accounts/replaceAll";
const ACCOUNT_ADDED = "accounts/addOne";
const ACCOUNTS_ADDED = "accounts/addMany";
const ACCOUNT_REPLACED = "accounts/replaceOne";
const ACCOUNT_REMOVED = "accounts/removeOne";

const emptyAccountsState = {
  byId: {},
  allIds: [],
  selectedIds: [],
  withToken: [],
  counts: {
    fbGroupCount: 0,
    fbPageCount: 0,
    gmbCount: 0,
    igAllowed: 0,
    igCount: 0,
    igFollowers: 0,
    liCount: 0,
    otherAllowed: 0,
    otherCount: 0,
    piCount: 0,
    twCount: 0,
    ttCount: 0,
    usesSkedLink: false,
    ytCount: 0,
    notificationCount: 0,
    notificationAllowed: 0,
    maxAllowed: 0,
    totalCount: 0,
    pricingVersion: "",
  },
};

export const replaceAllAccounts = createAction(
  ACCOUNTS_REPLACED,
  function prepare(accountsState: {
    ids: string[];
    byId: Record<string, AccountType>;
  }) {
    return {
      payload: accountsState,
    };
  }
);

export const addAccount = createAction<AccountType>(ACCOUNT_ADDED);
export const addAccounts = createAction<AccountType[]>(ACCOUNTS_ADDED);
export const replaceAccount = createAction<AccountType>(ACCOUNT_REPLACED);
export const removeAccount = createAction<string>(ACCOUNT_REMOVED);

export const selectAccountsState = (state: ReduxRootState) => {
  const accountsState = state.entities?.accounts;
  const byId = accountsState?.byId ?? {};

  return {
    ...emptyAccountsState,
    ...accountsState,
    byId,
    allIds: accountsState?.allIds ?? Object.keys(byId),
  };
};

export const selectAccountsById = createSelector(
  selectAccountsState,
  (accountsState) => accountsState.byId
);

export const selectAccountIds = createSelector(
  selectAccountsState,
  (accountsState) => accountsState.allIds
);

export const selectAllAccounts = createSelector(
  selectAccountsById,
  selectAccountIds,
  (byId, allIds) =>
    allIds
      .map((id) => byId[id])
      .filter((account): account is AccountType => Boolean(account))
);

export const selectAccountById = (state: ReduxRootState, accountId: string) =>
  selectAccountsById(state)[accountId] ?? null;

export const selectInstagramAccounts = createSelector(
  selectAllAccounts,
  (accounts) =>
    accounts.filter((account) => getAccountPlatformType(account) === "IG")
);

export const selectAccountsByGroups = createSelector(
  selectAllAccounts,
  (accounts) =>
    groupAccounts(accounts.filter((account) => accountHasGroup(account)))
);

export const selectAccountsWithoutGroups = createSelector(
  selectAllAccounts,
  (accounts) =>
    sortAccounts(accounts.filter((account) => !accountHasGroup(account)))
);
